import React, { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { getProblemTransitionVariants } from "../../utils/pageTransition";
import { useNavigate } from "react-router-dom";
import { Capacitor } from '@capacitor/core';
import { FaPencilAlt, FaRegLightbulb, FaRegFileAlt } from "react-icons/fa"; 
import { FiTarget, FiChevronRight } from "react-icons/fi";
import orzelLogo from "../../img/orzel_logo_192.png";
import { AiOutlineArrowLeft } from "react-icons/ai"; 

// --- CUSTOM HOOKS & UTILS ---
import { useWindowSize } from "../../hooks/useWindowSize";
import { useProblemSolver } from "../../hooks/useProblemSolver";
import { useOpenProblemFlow } from "../../hooks/useOpenProblemFlow";
import { OpenProblemDecision, OpenProblemLadder } from "../molecules/OpenProblemFlow";
import api from "../../api/apiHelper";
import { useAudio } from "../../hooks/useAudio";
import { useAiChat } from "../../hooks/useAiChat";
import { triggerHaptic } from "../../utils/haptics";
import { useAndroidSafeArea } from "../../hooks/useAndroidSafeArea";
import { RenderMarkdownWithLatex } from "../../utils/utils";

// --- COMPONENTS ---
import PageTestPage from "../molecules/PageTestPage";
import { readStoredStatus } from "../molecules/Page";
import DailyGoalSuccessPopup from "../molecules/DailyGoalSuccessPopup";
import AiChatPopup from "../molecules/AiChatPopup";
import SolutionStepper from "../molecules/SolutionStepper";
import HintAndFormulas from "../molecules/HintAndFormulas";
import ScratchpadModal from "../molecules/ScratchpadModal";
import HintPopup from "../molecules/HintPopup"; 
import SurefireSuccessPopup from "../molecules/SurefireSuccessPopup";
import SurefireBatchEndPopup from "../molecules/SurefireBatchEndPopup";
import { invalidateApiCache, writeApiCache, expireApiCache } from '../../utils/apiCache';

// --- ANIMACJE ---
// --- ANIMACJA PANELU (FEEL Z NAWIGACJI: FADE + LEKKIE UNIESIENIE) ---
// Przejście między zadaniami = getProblemTransitionVariants (wspólny moduł, spring + subtelny scale,
// spójne z nawigacją, lekko spokojniejsze). Definiowane w komponencie z useReducedMotion.
const buttonTransitionVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.4, ease: "easeOut" } },
    exit: { opacity: 0, transition: { duration: 0.2 } }
};

// --- ANIMACJA FEEDBACKU ODPOWIEDZI (POP / SHAKE) — jak w SolveProblem ---
const answerFeedbackVariants = {
    idle: { scale: 1, x: 0 },
    correct: { scale: [1, 1.06, 1], transition: { duration: 0.4, ease: "easeOut", times: [0, 0.4, 1] } },
    incorrect: { x: [0, -8, 8, -6, 6, -3, 0], transition: { duration: 0.4, ease: "easeInOut" } }
};

const SolveSurefire = () => {
    const navigate = useNavigate();
    const { width } = useWindowSize();
    
    // --- CONTEXT PEWNIAKA ---
    const [atomInfo] = useState(() => {
        try { return JSON.parse(localStorage.getItem("currentSurefireAtom")); } catch { return null; }
    });

    // --- WYKRYWANIE PLATFORMY ---
    const platform = Capacitor.getPlatform();
    const isIOS = platform === 'ios';
    const prefersReducedMotion = useReducedMotion();
    const problemTransitionVariants = getProblemTransitionVariants(prefersReducedMotion);

    // --- LOGIKA HOOKA ---
    const {
        currentProblem, problemsSession, answers, selectedAnswerText,
        wasLastAnswerCorrect,
        isAnswerChecked, showSolution, isHintUsed,
        solvedToday, dailyGoal,
        handleAnswerClick, handleSolution,
        handleHint, handleNextProblem, setShowSolution, shouldRequestReview,
        lastSubmissionResult,
        problemNumber,
        currentStreak,
        reinitialize,
    } = useProblemSolver({ isSurefireMode: true });

    const openFlow = useOpenProblemFlow({ currentProblem, handleAnswerClick, handleSolution });
    const { isOpen: isOpenProblem, openLadder } = openFlow;

    const playCorrectSound = useAudio('/audio/correct-answer.mp3');
    const {
        isChatOpen, chatHistory, isResponding, remainingCalls, maxCalls,
        handleOpenChat, handleSendMessage, setIsChatOpen, resetChat
    } = useAiChat(currentProblem, selectedAnswerText);

    // --- SAFE AREA LOGIC (czyste env(), inset-aware na każdej platformie) ---
    const { paddingBottom } = useAndroidSafeArea();

    // main (PageTestPage) rezerwuje juz safe-area jako paddingBottom, wiec stopka NIE moze
    // ponownie dodawac env() — to dublowalo inset i wynosilo stopke "w kosmos".
    const stickyBottomStyle = isIOS ? '1rem' : '1.5rem';

    const fabBottomPosition = isIOS
        ? `calc(9rem + ${paddingBottom})`
        : `calc(8rem + ${paddingBottom})`;
    
    // --- STATE UI ---
    const [accountStatus, setAccountStatus] = useState(readStoredStatus);
    const [popupState, setPopupState] = useState({ isOpen: false, completionLevel: 0 });
    const [isBatchEndOpen, setIsBatchEndOpen] = useState(false);
    const [isBatchLoading, setIsBatchLoading] = useState(false);

    const handleSurefireNext = useCallback(async () => {
        const isEnd = await handleNextProblem();
        if (isEnd) setIsBatchEndOpen(true);
    }, [handleNextProblem]);

    const handleContinueBatch = useCallback(async () => {
        setIsBatchLoading(true);
        try {
            const atomData = JSON.parse(localStorage.getItem("currentSurefireAtom"));
            const res = await api.post(`surefire-atoms/${atomData?.id}/start/`, {}, { skipLoading: true });
            const { final_problems, user_elo_dict } = res.data;
            localStorage.removeItem("problemsSession");
            localStorage.setItem("problemsData", JSON.stringify(final_problems));
            localStorage.setItem("userEloDict", JSON.stringify(user_elo_dict));
            setIsBatchEndOpen(false);
            reinitialize();
        } catch (err) {
            console.error("Błąd ładowania nowej paczki:", err);
        } finally {
            setIsBatchLoading(false);
        }
    }, [reinitialize]);

    const handleBackToSurefires = useCallback(() => {
        invalidateApiCache('user_dashboard_data', false);
        expireApiCache('surefire-atoms');
        api.get('user_stats_view/', { skipLoading: true }).then(r => writeApiCache('user_stats', r.data)).catch(() => {});
        api.get('fields_preferences/', { skipLoading: true }).then(r => writeApiCache('fields_preferences', r.data)).catch(() => {});
        navigate('/surefires');
    }, [navigate]);
    
    // --- STATE DLA POPUPA SUKCESU PEWNIAKA ---
    const [surefireSuccessState, setSurefireSuccessState] = useState({
        isOpen: false,
        message: "",
        subMessage: ""
    });

    const prevSolvedCountRef = useRef(0);
    const progressContainerRef = useRef(null);
    const [sketches, setSketches] = useState({});
    const [isScratchpadOpen, setIsScratchpadOpen] = useState(false);
    
    const [isHintPopupOpen, setIsHintPopupOpen] = useState(false);
    const [showBadgeTooltip, setShowBadgeTooltip] = useState(false);
    const badgeTooltipRef = useRef(null);

    const handleLightbulbClick = () => {
        handleHint(); 
        setIsHintPopupOpen(true);
    };
    
    const handleFormulaClick = () => {
        handleHint(); 
    };

    const handleSaveSketch = (problemId, paths) => {
        setSketches(prev => ({ ...prev, [problemId]: paths }));
    };
    
    // --- BADGE CONFIG ---
    const surefireBadgeConfig = useMemo(() => {
        if (!atomInfo) return null;
        return {
            text: 'PEWNIAK',
            style: "bg-gray-100 border-gray-200 text-gray-500 lg:pl-8 lg:pr-6 lg:py-2",
            icon: <FiTarget size={11} className="opacity-60" />,
            description: atomInfo.instruction || "Rozwiąż zadanie zgodnie ze schematem." 
        };
    }, [atomInfo]);

    // --- EFEKTY ---

    // 1. GŁÓWNA LOGIKA: Wykrywanie ukończenia fazy na podstawie lastSubmissionResult
   useEffect(() => {
        if (isAnswerChecked && lastSubmissionResult) {
            const surefireData = lastSubmissionResult.surefire_progress;
            
            if (surefireData && Array.isArray(surefireData)) {
                const completedAtom = surefireData.find(item => item.phase_completed === true);
                
                if (completedAtom) {
                    // NOWE: Pobieramy aktualny progress z backendu
                    // Jeśli backend nie zwraca current_progress, próbujemy obliczyć:
                    let currentProgress = completedAtom.current_progress;

                    // Fallback:
                    if (!currentProgress) {
                        if (completedAtom.completion_message?.includes("Utrwalenia")) {
                            currentProgress = 5;  // Faza 2 = WSZYSTKIE 5 kuleczek!
                        } else {
                            currentProgress = 3;
                        }
                    }
                    
                    const timer = setTimeout(() => {
                        setSurefireSuccessState({
                            isOpen: true,
                            message: completedAtom.completion_message || "Faza Zaliczona!",
                            subMessage: "Świetna robota! Pewniak przechodzi na wyższy poziom.",
                            progress: currentProgress  // NOWE: przekazujemy progress
                        });
                    }, 800);
                    return () => clearTimeout(timer);
                }
            }
        }
    }, [isAnswerChecked, lastSubmissionResult]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (badgeTooltipRef.current && !badgeTooltipRef.current.contains(e.target)) {
                setShowBadgeTooltip(false);
            }
        };
        if (showBadgeTooltip) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showBadgeTooltip]);

    useEffect(() => {
        if (isAnswerChecked) {
            const selectedAnswerObject = answers.find(answer => answer.text === selectedAnswerText);
            if (selectedAnswerObject && selectedAnswerObject.isCorrect) {
                playCorrectSound();
                triggerHaptic("success");
            } else {
                triggerHaptic("error");
            }
        }
    }, [isAnswerChecked, answers, selectedAnswerText, playCorrectSound]);

    useEffect(() => {
        if (isAnswerChecked) {
            const savedStatus = localStorage.getItem("userStatus");
            if (savedStatus) {
                setAccountStatus(JSON.parse(savedStatus));
            }
        }
    }, [isAnswerChecked]);

    const dailyGoalProgress = dailyGoal > 0
        ? Math.min(100, (solvedToday / dailyGoal) * 100 + 5)
        : 0;

    // Standardowy popup dzienny
    useEffect(() => {
        if (isAnswerChecked && dailyGoal > 0) {
            const prevCompletionLevel = Math.floor(prevSolvedCountRef.current / dailyGoal);
            const currentCompletionLevel = Math.floor(solvedToday / dailyGoal); 
            if (currentCompletionLevel > prevCompletionLevel && currentCompletionLevel <= 3) {
                setPopupState({ isOpen: true, completionLevel: currentCompletionLevel });
            }
        }
        prevSolvedCountRef.current = solvedToday;
    }, [isAnswerChecked, solvedToday, dailyGoal]);

    useEffect(() => {
        if (currentProblem) resetChat();
    }, [problemNumber, resetChat, currentProblem]);

    const getVisualLengthEstimate = (text) => {
        if (!text) return 0;
        return text.replace(/\\[a-zA-Z]+|\$|{|}/g, '').length;
    };

    const gridClass = useMemo(() => {
        if (!answers) return "grid-cols-1";
        let dynamicThreshold = width < 768 ? 15 : 40;
        const isAnyAnswerLong = answers.some(answer => getVisualLengthEstimate(answer.text) > dynamicThreshold);
        return isAnyAnswerLong ? "grid-cols-1" : "grid-cols-2";
    }, [answers, width]);


    if (!currentProblem) {
        return (
            <PageTestPage showNav={false} accountStatus={accountStatus} dailyGoalProgress={dailyGoalProgress}>
                <div className="text-center py-10"><p className="text-xl text-gray-600">Ładowanie Pewniaka...</p></div>
                <SurefireSuccessPopup
                    isOpen={surefireSuccessState.isOpen}
                    message={surefireSuccessState.message}
                    subMessage={surefireSuccessState.subMessage}
                    progress={surefireSuccessState.progress}
                    onConfirm={() => setSurefireSuccessState({ isOpen: false, message: "", subMessage: "" })}
                    onDismiss={() => setSurefireSuccessState({ isOpen: false, message: "", subMessage: "" })}
                />
            </PageTestPage>
        );
    }

    return (
        <PageTestPage
            showNav={false}
            reportIssueData={{ problem_id: currentProblem.problem_id, field_id: currentProblem.field_id }}
            accountStatus={accountStatus} 
            dailyGoalProgress={dailyGoalProgress}
        >
            {/* --- MODAL BRUDNOPISU --- */}
            {currentProblem && (
                <ScratchpadModal
                    isOpen={isScratchpadOpen}
                    onClose={() => setIsScratchpadOpen(false)}
                    problem={currentProblem}
                    initialPaths={sketches[currentProblem.problem_id]} 
                    onSave={handleSaveSketch}
                />
            )}

            {/* --- FAB (PRZYCISK BRUDNOPISU) --- */}
            <button
                onClick={() => setIsScratchpadOpen(true)}
                className="fixed right-4 z-40 bg-[#233855] text-white p-4 rounded-full shadow-[0_4px_14px_0_rgba(0,0,0,0.39)] hover:bg-[#345081] transition-transform hover:scale-105 active:scale-95 border-2 border-[#E09F2E]"
                style={{ bottom: fabBottomPosition }}
                aria-label="Otwórz brudnopis"
            >
                <FaPencilAlt size={20} />
            </button>

            {/* --- 1. POPUP SUKCESU PEWNIAKA (PRIORYTET) --- */}
            {popupState.isOpen && (
                <DailyGoalSuccessPopup
                    completionLevel={popupState.completionLevel}
                    onClose={() => setPopupState({ isOpen: false, completionLevel: 0 })}
                    isFirstSession={accountStatus?.current_streak === 0}
                    shouldRequestReview={shouldRequestReview}
                    streakDay={currentStreak}  
                />
            )}

            {/* --- POPUP KONIEC PACZKI --- */}
            <SurefireBatchEndPopup
                isOpen={isBatchEndOpen}
                atomName={atomInfo?.title}
                onContinue={handleContinueBatch}
                onFinish={handleBackToSurefires}
                isLoading={isBatchLoading}
            />

            {/* --- 2. POPUP SUKCESU PEWNIAKA (Zablokowany, dopóki popup dzienny jest otwarty) --- */}
            <SurefireSuccessPopup
                isOpen={surefireSuccessState.isOpen && !popupState.isOpen}
                message={surefireSuccessState.message}
                subMessage={surefireSuccessState.subMessage}
                progress={surefireSuccessState.progress}
                onConfirm={handleBackToSurefires}
                onDismiss={() => setSurefireSuccessState({ isOpen: false, message: "", subMessage: "" })}
                onShowSolution={() => {
                    setSurefireSuccessState({ isOpen: false, message: "", subMessage: "" });
                    setShowSolution(true);
                }}
            />

            {/* --- GŁÓWNA TREŚĆ --- */}
            <div className="flex flex-col max-w-2xl mx-auto w-full flex-grow px-4 lg:px-0">
                
                {/* BUTTON: WRÓĆ DO LISTY */}
                <div className="w-full flex mt-[3vh] lg:mt-[8vh] mb-2">
                    <button 
                        onClick={handleBackToSurefires}
                        className="text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1 text-sm font-medium"
                    >
                        <AiOutlineArrowLeft /> Wróć do listy pewniaków
                    </button>
                </div>

                {/* --- PANEL ZADANIA — CAŁOŚĆ (karta + rozwiązanie + akcje) PRZESUWA SIĘ RAZEM --- */}
                {/* Pager: przesuw przycina overflow-x-hidden na <main> (PageTestPage). Wrapper bez paddingu = karta wyśrodkowana, cień nietknięty. */}
                <div className="relative w-full mt-4">
                <AnimatePresence mode="wait" initial={false}>
                <motion.div
                    key={currentProblem.problem_id}
                    variants={problemTransitionVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    className="w-full"
                >
                {/* --- BIAŁA KARTA (TREŚĆ ZADANIA) --- */}
                <div className="bg-white rounded-xl lg:rounded-[3rem] shadow-[4px_4px_12px_rgba(0,0,0,0.30)] border border-gray-200 py-6 px-5 lg:px-14 w-full overflow-hidden relative"
                >

                    {/* --- BADGE PEWNIAKA --- */}
                    {surefireBadgeConfig && (
                        <div 
                            ref={badgeTooltipRef}
                            onClick={() => setShowBadgeTooltip(!showBadgeTooltip)}
                            className="absolute top-0 left-0 z-30 cursor-pointer group"
                        >
                            <div className={`
                                px-3 py-1 rounded-br-2xl border-b border-r
                                flex items-center gap-1.5 transition-opacity group-hover:opacity-100
                                ${surefireBadgeConfig.style}
                            `}>
                                {surefireBadgeConfig.icon}
                                <span className="text-[8px] font-bold font-apotek tracking-[0.15em] uppercase leading-none pt-0.5">
                                    {surefireBadgeConfig.text}
                                </span>
                            </div>
                            <AnimatePresence>
                                {showBadgeTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -5 }}
                                        animate={{ opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.22, 1, 0.36, 1] } }}
                                        exit={{ opacity: 0, transition: { duration: 0.1 } }}
                                        className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 shadow-xl rounded-lg p-3 z-50 text-left"
                                    >
                                        <p className="text-[10px] font-bold text-gray-800 uppercase tracking-wider mb-1 font-apotek">
                                            Pewniak maturalny
                                        </p>
                                        <p className="text-xs text-gray-700 leading-relaxed font-normal">
                                            Najczęstsze, najbardziej schematyczne zadania z arkuszy CKE.
                                        </p>
                                        <div className="absolute -top-[5px] left-4 w-2 h-2 bg-white border-t border-l border-gray-200 transform rotate-45"></div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}

                    {/* --- IKONA ŻARÓWKI --- */}
                    {currentProblem.hint && currentProblem.hint.trim() !== "" && (
                        <button
                            onClick={handleLightbulbClick}
                            className="absolute top-4 right-4 text-[#E09F2E] hover:text-[#b88226] hover:scale-110 transition-all p-2 z-10"
                        >
                            <FaRegLightbulb size={24} strokeWidth={1} className="drop-shadow-sm" />
                        </button>
                    )}

                    {/* --- WARSTWA TREŚCI (układ pionowy) --- */}
                    <div className="w-full space-y-6">
                            {/* Header Zadania */}
                            <div className="w-full flex justify-center items-center text-center mb-2 lg:mt-[0.5vh]">
                                <span className="text-[#233855] font-apotek text-xs lg:xs tracking-widest uppercase font-normal">
                                    {currentProblem.atom_name || atomInfo?.title || "PEWNIAK"}
                                </span>
                            </div>

                            {/* Pytanie */}
                            <p className="text-base lg:text-normal font-normal text-gray-900 text-center min-h-[50px] flex items-center justify-center lg:mt-[0.5vh]">
                                <RenderMarkdownWithLatex text={!currentProblem.is_mobile && currentProblem.points_assigned ? `(${currentProblem.points_assigned} pkt) ${currentProblem.question}` : currentProblem.question} />
                            </p>

                            {/* Obrazek */}
                            {currentProblem.image_link && (
                                <div className="flex justify-center py-2">
                                    <img
                                        src={
                                            currentProblem.image_link.startsWith('http')
                                                ? currentProblem.image_link
                                                : `${process.env.REACT_APP_STATIC_FILES_SOURCE || ''}/images/${currentProblem.image_link}`
                                        }
                                        alt="Ilustracja zadania"
                                        className="max-w-full h-auto rounded border"
                                    />
                                </div>
                            )}

                            {/* KARTA WZORÓW / HINTY */}
                            <div className="w-full">
                                {(() => {
                                    const hasLinkedFormulas = currentProblem.linked_formulas && currentProblem.linked_formulas.length > 0;
                                    if (!hasLinkedFormulas) return null;
                                    if (isHintUsed) {
                                        return (
                                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                                <HintAndFormulas 
                                                    hintText={null} 
                                                    formulas={currentProblem.linked_formulas} 
                                                />
                                            </div>
                                        );
                                    } else {
                                        return (
                                            <button
                                                onClick={handleFormulaClick}
                                                className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] mb-2"
                                            >
                                                <div className="w-9 h-9 rounded-lg bg-[#233855] flex items-center justify-center flex-shrink-0">
                                                    <FaRegFileAlt size={16} className="text-[#E09F2E]" />
                                                </div>
                                                <div className="flex-1 text-left min-w-0">
                                                    <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Karta Wzorów</span>
                                                    <span className="block text-xs text-gray-400 leading-tight">Otwórz wzory do zadania</span>
                                                </div>
                                                <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                                    <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                            </button>
                                        );
                                    }
                                })()}
                            </div>

                            {isOpenProblem && (
                                <OpenProblemDecision
                                    currentProblem={currentProblem}
                                    flow={openFlow}
                                    isAnswerChecked={isAnswerChecked}
                                    wasLastAnswerCorrect={wasLastAnswerCorrect}
                                    showSolution={showSolution}
                                    setShowSolution={setShowSolution}
                                />
                            )}

                            {/* GRID ODPOWIEDZI */}
                            {!isOpenProblem && (
                            <div className={`grid ${gridClass} gap-2 w-full`}>
                                {answers.map((answer, index) => {
                                    const isCorrectAndChecked = isAnswerChecked && answer.isCorrect;
                                    const isIncorrectSelected = isAnswerChecked && !answer.isCorrect && selectedAnswerText === answer.text;
                                    let buttonClass = "flex items-center justify-center rounded-lg text-[0.8rem] text-slate-100 px-3 py-1.5 font-normal tracking-wider shadow-md transition-colors duration-200 disabled:cursor-not-allowed min-h-[20px] w-full";

                                    if (isCorrectAndChecked) buttonClass += " bg-[#007D00] ring-2 ring-offset-2 ring-[#007D00]";
                                    else if (isIncorrectSelected) buttonClass += " bg-[#8A0000] opacity-90";
                                    else if (isAnswerChecked) buttonClass += " bg-[#233855] opacity-40";
                                    else buttonClass += " bg-[#233855] hover:bg-[#345081]";

                                    const feedbackState = prefersReducedMotion
                                        ? "idle"
                                        : isCorrectAndChecked
                                            ? "correct"
                                            : isIncorrectSelected
                                                ? "incorrect"
                                                : "idle";

                                    return (
                                        <motion.button
                                            key={index}
                                            onClick={() => handleAnswerClick(answer)}
                                            disabled={isAnswerChecked}
                                            className={buttonClass}
                                            variants={answerFeedbackVariants}
                                            animate={feedbackState}
                                            whileTap={!isAnswerChecked ? { scale: 0.96 } : undefined}
                                        >
                                            <RenderMarkdownWithLatex text={answer.text} />
                                        </motion.button>
                                    );
                                })}
                            </div>
                            )}

                            {!isOpenProblem && !showSolution && (
                                <button
                                    onClick={isAnswerChecked ? () => setShowSolution(true) : handleSolution}
                                    className="w-full text-xs lg:text-xs font-uppercase font-apotek shadow-md tracking-widest bg-[#F0F0F0] border border-gray-300 text-gray-600 hover:bg-gray-50 px-4 py-1.5 rounded-lg font-medium transition-colors"
                                >
                                    ROZWIĄZANIE
                                </button>
                            )}
                        </div>
                </div>
                {/* --- /BIAŁA KARTA --- */}

                {/* POZA KARTĄ, ale W TYM SAMYM PANELU: kroki rozwiązania + Orzeł AI */}
                {showSolution && !(isOpenProblem && openLadder) && (
                    <div className="w-full mt-4 space-y-3 animate-in fade-in zoom-in duration-300">
                        <SolutionStepper solutionText={currentProblem.solution || "Brak rozwiązania."} />
                        <button
                            onClick={handleOpenChat}
                            disabled={isResponding}
                            className="group w-full flex items-center gap-3 bg-white border border-[#E09F2E]/40 px-4 py-3 rounded-xl shadow-sm hover:shadow-md hover:border-[#E09F2E] transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <div className="w-9 h-9 rounded-full bg-[#233855] flex items-center justify-center flex-shrink-0 overflow-hidden">
                                <img src={orzelLogo} alt="Orzeł AI" className="w-full h-full object-cover" />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                                <span className="block text-sm font-bold text-[#233855] font-apotek leading-tight">Zapytaj Orła AI</span>
                                <span className="block text-xs text-gray-400 leading-tight">Wyjaśni krok po kroku</span>
                            </div>
                            <div className="w-7 h-7 rounded-full bg-gray-100 group-hover:bg-[#233855]/10 flex items-center justify-center flex-shrink-0 transition-colors">
                                <FiChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#233855] group-hover:translate-x-0.5 transition-all" />
                            </div>
                        </button>
                    </div>
                )}

                {isOpenProblem && openLadder && (
                    <OpenProblemLadder
                        currentProblem={currentProblem}
                        flow={openFlow}
                        isAnswerChecked={isAnswerChecked}
                        onOpenChat={handleOpenChat}
                        isResponding={isResponding}
                        showBackupWidget={false}
                    />
                )}

                {/* PRZYCISK KOLEJNE ZADANIE */}
                <AnimatePresence>
                    {isAnswerChecked && (
                        <motion.div
                            key="next-button"
                            variants={buttonTransitionVariants}
                            initial="initial"
                            animate="animate"
                            exit="exit"
                            className="w-full pt-6 flex justify-center"
                        >
                            <button
                                onClick={handleSurefireNext}
                                className="flex w-full lg:w-[25rem] justify-center rounded-lg bg-[#233855] px-3 py-2 text-xs lg:text-xs font-apotek tracking-widest text-slate-100 shadow-lg hover:bg-[#345081] focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600"
                            >
                                KOLEJNE ZADANIE
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>
                </motion.div>
                </AnimatePresence>
                </div>
                {/* --- /PANEL ZADANIA --- */}
                <div className="h-[10vh] flex-shrink-0" />
            </div>

            <div className="flex-grow" />
            
            {/* --- STOPKA Z KROPKAMI --- */}
            <div 
                className="sticky w-full pt-3 z-30 lg:bottom-[5vh]" 
                style={{ bottom: stickyBottomStyle }}
            >
                {/* <div className="max-w-xl mx-auto space-y-5 p-2">
                    {!currentProblem.is_mobile && currentProblem?.linked_matura_problems?.length > 0 && (
                        <div className="w-full text-center text-xs text-gray-500 italic">
                            Na podstawie: {currentProblem.linked_matura_problems.map(linkedPb => linkedPb.source).join(', ')}
                        </div>
                    )}
                </div> */}
            </div>

            {/* --- POPUPY (AI, HINT) --- */}
            <AiChatPopup
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                chatHistory={chatHistory}
                onSendMessage={handleSendMessage}
                isResponding={isResponding}
                problemQuestion={currentProblem?.question}
                remainingCalls={remainingCalls}
                maxCalls={maxCalls}
            />
            <HintPopup 
                isOpen={isHintPopupOpen} 
                onClose={() => setIsHintPopupOpen(false)} 
                hintText={currentProblem.hint} 
            />
        </PageTestPage>
    );
};

export default SolveSurefire;